
import { useState } from 'react';

export default function PortfolioSection() {
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'patios', label: 'Patios' },
    { id: 'walls', label: 'Retaining Walls' },
    { id: 'renders', label: '3D Renders' },
    { id: 'turf', label: 'Turf & Sod' },
    { id: 'lighting', label: 'Lighting' }
  ];

  const projects = [
    {
      id: 1,
      category: 'renders',
      title: '3D Renders',
      description: 'Luxury outdoor living space with integrated seating and fire feature',
      image: 'https://public.readdy.ai/ai/img_res/bf0e79f15ea575e67932d22daf0db57f.jpg',
      type: '3D Render'
    },
    {
      id: 2,
      category: 'patios',
      title: 'Paver Patio',
      description: 'Natural stone patio with custom seating walls and integrated planters',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20completed%20stone%20patio%20project%20with%20natural%20stone%20pavers%2C%20elegant%20outdoor%20furniture%2C%20integrated%20planters%2C%20and%20professional%20landscaping%20creating%20a%20sophisticated%20outdoor%20entertainment%20area&width=600&height=400&seq=patio-1&orientation=landscape',
      type: 'Completed Project'
    },
    {
      id: 3,
      category: 'walls',
      title: 'Terraced Retaining Wall System',
      description: 'Multi-level retaining walls with integrated drainage and lighting',
      image: 'https://static.readdy.ai/image/f93d48f1419f4c68f20bbda738a4241b/49288fcc7a9f512366f282a8aba4f283.jpeg',
      type: 'Completed Project'
    },
    {
      id: 4,
      category: 'turf',
      title: 'Premium Turf Installation',
      description: 'Artificial turf with natural stone borders and drainage system',
      image: 'https://static.readdy.ai/image/f93d48f1419f4c68f20bbda738a4241b/448a5f3d035f6ff75de0c8b338847cdd.jpeg',
      type: 'Completed Project'
    },
    {
      id: 5,
      category: 'renders',
      title: 'Pool Area 3D Design',
      description: 'Comprehensive pool area design with decking and landscape integration',
      image: 'https://readdy.ai/api/search-image?query=Stunning%203D%20architectural%20render%20of%20luxury%20pool%20area%20with%20modern%20decking%2C%20integrated%20landscaping%2C%20ambient%20lighting%2C%20and%20premium%20outdoor%20furniture%20showcasing%20complete%20pool%20environment%20design&width=600&height=400&seq=render-2&orientation=landscape',
      type: '3D Render'
    },
    {
      id: 6,
      category: 'lighting',
      title: 'Outdoor Lighting Design',
      description: 'Comprehensive outdoor lighting system with pathway and accent lights',
      image: 'https://readdy.ai/api/search-image?query=Professional%20landscape%20lighting%20installation%20with%20pathway%20lights%2C%20accent%20lighting%20on%20plants%20and%20structures%2C%20warm%20LED%20illumination%20creating%20beautiful%20nighttime%20outdoor%20ambiance%20and%20security&width=600&height=400&seq=lighting-1&orientation=landscape',
      type: 'Completed Project'
    }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <section id="portfolio" className="py-20 bg-[#F5F5F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1C1C1C] mb-6 font-['Playfair_Display']">
            Our Portfolio
          </h2>
          <p className="text-xl text-[#1C1C1C]/70 max-w-3xl mx-auto mb-8">
            Explore our collection of completed projects and 3D design renderings that showcase 
            our commitment to luxury outdoor living spaces.
          </p>

          {/* Filter buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === filter.id
                    ? 'bg-[#4B6043] text-white shadow-lg'
                    : 'bg-white text-[#1C1C1C] hover:bg-[#D9C5A0] hover:text-[#1C1C1C]'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Project grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div 
              key={project.id}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
            >
              <div className="relative overflow-hidden">
                <img 
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4">
                  <span className="bg-[#4B6043] text-white px-3 py-1 rounded-full text-sm">
                    {project.type}
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-semibold text-[#1C1C1C] mb-3 font-['Playfair_Display']">
                  {project.title}
                </h3>
                <p className="text-[#1C1C1C]/70 leading-relaxed">
                  {project.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-[#4B6043] text-white px-8 py-4 rounded-lg hover:bg-[#3a4d35] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-image-line mr-2"></i>
            Start Your Project
          </button>
        </div>
      </div>
    </section>
  );
}
